import { useTranslation } from 'react-i18next';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useMembersQuery } from '@platform/state/api';
import { FilterChip } from '../filters/FilterChip';
import { isComplete, valueKindOf } from './advancedFilter';
import { conditionLabel } from './conditionLabel';
import { fieldOptions } from './fieldOptions';
import type { IAdvancedChipsProps } from './IAdvancedChipsProps';
import { useQuickFieldLabel } from './useQuickFieldLabel';
import { useQuickFieldOptions } from './useQuickFieldOptions';

/**
 * Чипи застосованих розширених фільтрів у рядку фільтрів: «Поле: значення ×» для вибраних швидких полів і «Поле умова значення ×» для
 * кожного повного рядка умов (неповні не фільтрують і не показуються); × прибирає саме цей фільтр. Значення — підписи варіантів поля,
 * імена учасників, «Я», «Так/Ні».
 */
export function AdvancedChips({ ws, rt, conditions, onChange, quick, onQuick }: IAdvancedChipsProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const quickOptions = useQuickFieldOptions(ws, rt);
  const quickLabel = useQuickFieldLabel(rt);
  const users = conditions.some((c) => isComplete(c) && rt.fields.find((f) => f.key === c.field)?.type === 'user');
  const { data: members = [] } = useMembersQuery(ws, { skip: !users });
  const fieldLabel = (key: string) => strings.field(rt.key, key, rt.fields.find((f) => f.key === key)?.label);
  const valueOf = (field: (typeof rt.fields)[number] | undefined, kind: string, value: string): string => {
    if (kind === 'option') return strings.option(rt.key, field?.key ?? '', value, fieldOptions(rt, field).find((o) => o.value === value)?.label ?? null);
    if (kind === 'user') return value === 'me' ? t('listbar.me') : members.find((m) => m.user.id === value)?.user.name ?? value;
    if (kind === 'boolean') return value === 'true' ? t('app.yes') : t('app.no');
    return value;
  };
  const dropQuick = (key: string) => onQuick(Object.fromEntries(Object.entries(quick).filter(([k]) => k !== key)));
  return (
    <>
      {Object.entries(quick).filter(([, v]) => v).map(([key, value]) => (
        <FilterChip key={`q-${key}`} clearLabel={t('listbar.clearFilter')} onClear={() => dropQuick(key)}
          label={t('quickFields.chip', { field: quickLabel(key), value: (quickOptions(key).find((o) => o.value === value)?.label ?? value).trim() })} />
      ))}
      {conditions.map((c, i) => {
        if (!isComplete(c)) return null;
        const field = rt.fields.find((f) => f.key === c.field);
        const kind = valueKindOf(field, c.op);
        const label = conditionLabel(field ? fieldLabel(field.key) : c.field, t(`listbar.ops.${c.op}`), kind === 'none' ? null : valueOf(field, kind, c.value), kind === 'text');
        return <FilterChip key={`${i}-${c.field}-${c.op}`} label={label} clearLabel={t('listbar.removeCondition')} onClear={() => onChange(conditions.filter((_, j) => j !== i))} />;
      })}
    </>
  );
}
